// client-error-reporter.ts — observabilité front (DC-1).
//
// Capture les erreurs JS non gérées (`window.onerror`) et les rejets de
// promesse non interceptés (`unhandledrejection`), et les POST vers
// `/api/v1/client-errors`. Le backend les journalise en JSON structuré
// → stdout → Log Analytics (cf. app/routers/client_errors.py).
//
// Exception ASSUMÉE à DT2 ("fetch direct → wrapper api.js") : un reporter
// d'erreurs ne doit PAS dépendre de la couche `api` (qui peut justement être
// la source de l'erreur), ni déclencher l'UI de loading/toast du wrapper.
// On utilise donc `fetch` brut, en avalant toute exception pour ne jamais
// transformer le reporter lui-même en source d'erreur (boucle).
//
// Garde-fous anti-bruit / anti-abus :
//   - cap par session (MAX_REPORTS_PER_SESSION) ;
//   - déduplication des erreurs identiques (même message/source/ligne) ;
//   - le backend est en plus rate-limité (30/min/IP).

const ENDPOINT = '/api/v1/client-errors';
const MAX_REPORTS_PER_SESSION = 20;

let sent = 0;
const seen = new Set<string>();

// `| undefined` explicite sur les optionnels : le repo active
// `exactOptionalPropertyTypes`, qui interdit d'affecter `undefined` à un
// `field?: string` sans cette union.
interface ClientErrorPayload {
  message: string;
  kind: 'error' | 'unhandledrejection';
  source?: string | undefined;
  line?: number | undefined;
  col?: number | undefined;
  stack?: string | undefined;
  page_url?: string | undefined;
}

function report(payload: ClientErrorPayload): void {
  if (sent >= MAX_REPORTS_PER_SESSION) return;
  const key = `${payload.kind}:${payload.message}:${payload.source ?? ''}:${payload.line ?? ''}`;
  if (seen.has(key)) return;
  seen.add(key);
  sent += 1;

  try {
    // keepalive : laisse la requête se terminer même si la page se ferme.
    void fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* swallow : le reporter ne doit jamais lever ni boucler */
    });
  } catch {
    /* swallow */
  }
}

window.addEventListener('error', (event: ErrorEvent): void => {
  // Les erreurs de chargement de ressource (img/script) bubblent ici sans
  // message ni Error : on les ignore pour ne pas polluer avec "Unknown error".
  if (!event.message && !event.error) return;
  report({
    kind: 'error',
    message: event.message || 'Unknown error',
    source: event.filename || undefined,
    line: event.lineno || undefined,
    col: event.colno || undefined,
    stack: event.error instanceof Error ? event.error.stack : undefined,
    page_url: location.href,
  });
});

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
  const reason: unknown = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  report({
    kind: 'unhandledrejection',
    message: message || 'Unhandled promise rejection',
    stack: reason instanceof Error ? reason.stack : undefined,
    page_url: location.href,
  });
});
